import { useEffect, useMemo, useRef, useState } from 'react';
import { ThemeProvider } from '@mui/material';

import {
  FormNames,
  HighSchoolTeacherOrCollegeInstructor,
  SubjectAreaMapping,
  TeacherEvaluationDocument,
  TeacherEvaluationSchema,
  TeacherEvaluation as TeacherEvaluationType,
  TeacherProfileForSubmissionSchema,
  YesNo,
} from '@youscience/commonapp-service-common';
import { brightpathTheme } from '@youscience/theme';

import { TeacherTabSections } from '@interfaces/recommenderWorkspace';

import useRecommenderStore from '@store/RecommenderStore';
import useRecommenderWorkspaceStore from '@store/RecommenderWorkspaceStore';
import useNotificationStore from '@store/NotificationStore';

import { useApplicantForm } from '@pages/StudentDetailsPage/hooks/useApplicantForm';

import { formatDateWithFallback } from '@utils/formatter';

import { FormState } from '@constants/commonApp/types';

import { useUserProfile } from '@hooks/useUserProfile';

import FormHeader from '../../common/FormHeader';
import WrittenEvaluation from './WrittenEvaluation';
import Ratings from './Ratings';
import FormFooter from '../../common/FormFooter';
import ReviewPdf, { ReviewTypes } from '../../common/ReviewPdf';
import {
  checkFormValidity,
  PREVIEW_ERROR_NOTIFICATION,
  REVIEW_NOT_READY_ERROR_NOTIFICATION,
  onSaveErrorNotification,
  onSaveSuccessNotification,
  onUnsubmitSuccessNotification,
} from '../../common/formUtils';
import Submissions from '../../common/Submissions';
import { FormAvailabilityStatus, FormCompletionStatus } from '../../common/enums';

const formName = FormNames.TEACHER_EVALUATION;

export const TeacherEvaluation = () => {
  const { notify } = useNotificationStore((state) => state);

  const { userId } = useUserProfile();

  const { recommenderProfile } = useRecommenderStore((store) => store);

  const teacherEvaluation = useApplicantForm<TeacherEvaluationDocument & { memberId: number }>(formName);

  const showRatings = recommenderProfile?.teacherProfile?.academicRating === YesNo.Yes;

  const subjectAreaCode = useMemo(
    () =>
      Object.entries(
        SubjectAreaMapping as Record<TeacherEvaluationDocument['subjectArea']['subjectArea'], string>,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      ).find(([_, value]) => {
        return value === teacherEvaluation.metadata.subjectArea;
      })?.[0],
    [teacherEvaluation.metadata],
  );

  const initialFormState = useMemo(
    () => ({
      [TeacherTabSections.WrittenEvaluation]: teacherEvaluation.content?.writtenEvaluation,
      [TeacherTabSections.Ratings]: teacherEvaluation.content?.ratings,
      [TeacherTabSections.SubjectArea]: {
        subjectArea: subjectAreaCode,
      },
    }),
    [teacherEvaluation.content, showRatings, subjectAreaCode],
  );

  const form = useRef(initialFormState);

  useEffect(() => {
    form.current = { ...form.current, [TeacherTabSections.SubjectArea]: { subjectArea: subjectAreaCode! } };
  }, [subjectAreaCode]);

  const [formErrors, setFormErrors] = useState({});
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [reviewType, setReviewType] = useState<ReviewTypes>(ReviewTypes.PREVIEW_FORM);
  const [expandedSection, setExpandedSection] = useState<TeacherTabSections | null>(
    TeacherTabSections.WrittenEvaluation,
  );

  const isSubmitted =
    teacherEvaluation.metadata?.completionStatus === FormCompletionStatus.SUBMITTED ||
    teacherEvaluation.metadata?.completionStatus === FormCompletionStatus.DOWNLOADED;
  const isSubmitDisabled = teacherEvaluation.metadata?.availabilityStatus === FormAvailabilityStatus.NOT_AVAILABLE;
  const { applicantId, recommenderId } = teacherEvaluation.metadata;

  const toggleExpandedSelection = (section: TeacherTabSections) => {
    if (expandedSection === section) {
      setExpandedSection(null);
    } else {
      setExpandedSection(section);
    }
  };

  const getNextSection = (section: TeacherTabSections) => {
    if (section === TeacherTabSections.WrittenEvaluation) {
      return showRatings ? TeacherTabSections.Ratings : null;
    }

    return null;
  };

  const onFormChangeHandler =
    <K,>(section: TeacherTabSections) =>
    (sectionFormState: FormState<K>) => {
      form.current = { ...form.current, [section]: sectionFormState.data };
    };

  const validateForm = () => {
    const teacherProfileForValidation = {
      personalDetails: {
        firstName: recommenderProfile?.teacherProfile?.firstName,
        lastName: recommenderProfile?.teacherProfile?.lastName,
        email: recommenderProfile?.teacherProfile?.email,
        phone: recommenderProfile?.teacherProfile?.phone,
        position: recommenderProfile?.teacherProfile?.position,
      },
      schoolDetails: {
        teacherType:
          recommenderProfile?.teacherProfile?.teacherType ||
          HighSchoolTeacherOrCollegeInstructor['High school/secondary school teacher'],
        academicRating: recommenderProfile?.teacherProfile?.academicRating,
      },
    };

    const profileValid = checkFormValidity(teacherProfileForValidation, TeacherProfileForSubmissionSchema);
    const formValid = checkFormValidity(form.current, TeacherEvaluationSchema);

    return {
      form: !formValid,
      recommenderProfile: !profileValid,
    };
  };

  const handleSaveForLater = async (options?: {
    isHideNotification?: boolean;
    section?: keyof typeof initialFormState;
  }) => {
    const { isHideNotification, section } = options || {};

    const data = section ? { [section]: form.current[section] } : form.current;

    await teacherEvaluation.save({ ...data, applicantId, recommenderId } as TeacherEvaluationDocument);

    if (!isHideNotification) {
      teacherEvaluation.isError ? onSaveErrorNotification() : onSaveSuccessNotification(formName);
    }

    if (section && !teacherEvaluation.isError) {
      toggleExpandedSelection(section);
      const nextSection = getNextSection(section);
      if (nextSection) toggleExpandedSelection(nextSection);
    }
  };

  const previewForm = async () => {
    if (isSubmitted && recommenderId) {
      setIsPreviewOpen(true);
      await teacherEvaluation.getSubmitted({
        applicantId,
        recommenderId,
        memberId: teacherEvaluation.metadata?.members?.[0]?.memberId,
      });
    } else {
      await handleSaveForLater({ isHideNotification: true });

      const formErrors = validateForm();
      setFormErrors(formErrors);
      setIsPreviewOpen(true);
      const hasFormErrors = Object.values(formErrors).some(Boolean);
      if (!hasFormErrors) {
        await teacherEvaluation.preview({
          ...(form.current as TeacherEvaluationDocument),
          applicantId,
          recommenderId,
          memberId: teacherEvaluation.metadata?.members?.[0]?.memberId,
        });
      }

      useRecommenderWorkspaceStore.subscribe((state) => {
        if (state.isError) {
          notify(isSubmitted ? REVIEW_NOT_READY_ERROR_NOTIFICATION : PREVIEW_ERROR_NOTIFICATION);
          setIsPreviewOpen(false);
        }
      });
    }
  };

  const submitForm = async (signature: string, signedDate: string) => {
    const approval = { isReviewed: true, isAffirmed: true, signature, signedDate, userId: userId as string };

    await teacherEvaluation.submit({
      applicantId,
      recommenderId,
      teacherApproval: approval,
      counselorApproval: teacherEvaluation.metadata.isTeacherCounselor ? approval : undefined,
    });

    teacherEvaluation.isError
      ? onSaveErrorNotification()
      : notify({
          message: 'Teacher evaluation submitted successfully for counselor approval.',
          severity: 'success',
        });
  };

  const handleOnSubmit = () => {
    setReviewType(ReviewTypes.REVIEW_AND_SUBMIT);
    void previewForm();
  };

  const handleOnPreview = async () => {
    setReviewType(isSubmitted ? ReviewTypes.REVIEW_FORM : ReviewTypes.PREVIEW_FORM);
    void previewForm();
  };

  const handleOnSaveAndContinue = async (section: TeacherTabSections) => {
    await handleSaveForLater();

    if (section === TeacherTabSections.WrittenEvaluation) {
      toggleExpandedSelection(TeacherTabSections.Ratings);
    } else {
      toggleExpandedSelection(TeacherTabSections.WrittenEvaluation);
    }
  };

  const handleOnUnsubmit = async () => {
    await teacherEvaluation.unsubmit({ applicantId, recommenderId });

    teacherEvaluation.isError ? onSaveErrorNotification() : onUnsubmitSuccessNotification(formName);
  };

  if (!teacherEvaluation.isReady) {
    return null;
  }

  return (
    <ThemeProvider theme={brightpathTheme}>
      <FormHeader
        name="Teacher evaluation"
        tooltipTitle="Teachers write from the vantage point of the classroom. They can provide a view of an applicant’s intellectual curiosity, creative thought, and work habits."
        deadline={formatDateWithFallback(teacherEvaluation.metadata?.deadline)}
        status={teacherEvaluation.metadata?.completionStatus || FormCompletionStatus.NEW}
        onPreviewClick={handleOnPreview}
        onSaveForLaterClick={handleSaveForLater}
        onSubmitClick={handleOnSubmit}
        onUnsubmitClick={handleOnUnsubmit}
        areAllActionsDisabled={teacherEvaluation.isSavingForm}
        isSubmitDisabled={isSubmitDisabled}
        isFormSumbitted={isSubmitted || teacherEvaluation.metadata.completionStatus === FormCompletionStatus.PENDING}
        formName={formName}
      />
      {teacherEvaluation.metadata.completionStatus === FormCompletionStatus.SUBMITTED ||
      teacherEvaluation.metadata.completionStatus === FormCompletionStatus.PENDING ? (
        <Submissions
          formName={formName}
          onSubmit={handleOnSubmit}
          isPending={teacherEvaluation.metadata.completionStatus === FormCompletionStatus.PENDING}
          teacherId={teacherEvaluation.metadata.recommenderId}
        />
      ) : (
        <>
          <WrittenEvaluation
            writtenEvaluation={teacherEvaluation.content?.writtenEvaluation}
            isExtended={expandedSection === TeacherTabSections.WrittenEvaluation}
            onExpand={() => toggleExpandedSelection(TeacherTabSections.WrittenEvaluation)}
            onSaveAndContinue={
              showRatings ? () => handleOnSaveAndContinue(TeacherTabSections.WrittenEvaluation) : undefined
            }
            onFormChange={onFormChangeHandler<Partial<TeacherEvaluationType['writtenEvaluation']>>(
              TeacherTabSections.WrittenEvaluation,
            )}
            subjectArea={subjectAreaCode}
            isSaved={teacherEvaluation.sectionIsSaved.writtenEvaluation}
            applicantName={teacherEvaluation.metadata.studentName}
          />

          {showRatings ? (
            <Ratings
              ratings={teacherEvaluation.content?.ratings}
              isExtended={expandedSection === TeacherTabSections.Ratings}
              onExpand={() => toggleExpandedSelection(TeacherTabSections.Ratings)}
              onFormChange={onFormChangeHandler<Partial<TeacherEvaluationType['ratings']>>(TeacherTabSections.Ratings)}
              isSaved={teacherEvaluation.sectionIsSaved.ratings}
            />
          ) : null}
        </>
      )}

      <FormFooter
        onPreviewClick={handleOnPreview}
        onSaveForLaterClick={handleSaveForLater}
        onSubmitClick={handleOnSubmit}
        onUnsubmitClick={handleOnUnsubmit}
        areAllActionsDisabled={teacherEvaluation.isSavingForm}
        isSubmitDisabled={isSubmitDisabled}
        isFormSumbitted={isSubmitted || teacherEvaluation.metadata.completionStatus === FormCompletionStatus.PENDING}
        formName={formName}
      />

      {isPreviewOpen ? (
        <ReviewPdf
          title="Teacher evaluation"
          subtitle="Please review the PDF preview of your form. Scroll to the bottom if necessary to confirm and continue."
          isOpen={isPreviewOpen}
          isLoading={teacherEvaluation.isLoadingPreview}
          reviewType={reviewType}
          pdf={teacherEvaluation.previewPdfURL}
          onClose={() => setIsPreviewOpen(false)}
          onContinue={(signature?: string, date?: string) => {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            if (reviewType === ReviewTypes.REVIEW_AND_SUBMIT) submitForm(signature!, date!);
          }}
          errors={formErrors}
        />
      ) : null}
    </ThemeProvider>
  );
};
